import * as XLSX from "xlsx";
import {
  inspectTimetableAoa,
  validateParsedLessons,
  parseCell,
  excelRef,
  type InspectTimetableResult,
  type ParsedLesson,
  type ExcelImportIssue,
  type TimetableImportOptions,
  type SharedTeacherRow,
} from "./timetableExcelImportValidate";

const text = (value: unknown) =>
  String(value ?? "")
    .replace(/_x000D_/gi, "\n")
    .normalize("NFC")
    .trim();
export function canonicalClass(value: unknown): string | null {
  const s = text(value).replace(/\s/g, "");
  const m =
    s.match(/^([1-6])(?:학년|-|\.|_)(\d{1,2})반?$/) ??
    s.match(/^([1-6])(\d{2})$/);
  if (m && Number(m[2]) > 0 && Number(m[2]) <= 30)
    return `${Number(m[1])}-${Number(m[2])}`;
  return null;
}
// Only explicit, complete class identifiers are accepted. Never guess ranges
// such as “1-1~3” or drop an unrecognized token from a multi-class cell.
export function parseClassList(value: unknown): string[] | null {
  const parts = text(value).split(/[\r\n,，/;·]+/).map(text).filter(Boolean);
  if (!parts.length) return null;
  const classes = parts.map(canonicalClass);
  if (classes.some((room) => !room) || new Set(classes).size !== classes.length)
    return null;
  return classes as string[];
}
const day = (value: unknown) =>
  text(value).match(/^([월화수목금토일])(?:요일)?$/)?.[1];
const period = (value: unknown) => {
  const m = text(value).match(/^(\d{1,2})(?:교시)?$/);
  return m && Number(m[1]) > 0 && Number(m[1]) <= 12 ? Number(m[1]) : null;
};
const empty = () => validateParsedLessons([]);

export function inspectSchoolSheet(
  sheet: XLSX.WorkSheet,
  options: TimetableImportOptions = {},
): InspectTimetableResult {
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
    range: 0,
  });
  const header = aoa.findIndex(
    (row, i) => i < 50 && row.filter((v) => day(v)).length >= 2,
  );
  if (header < 0) return inspectTimetableAoa(aoa);
  let periods = header + 1;
  while (
    periods < Math.min(header + 4, aoa.length) &&
    aoa[periods].filter((v) => period(v)).length < 2
  )
    periods++;
  if (periods >= Math.min(header + 4, aoa.length))
    return inspectTimetableAoa(aoa);
  const dayRow = aoa[header];
  const first = dayRow.findIndex((v) => day(v));
  const teacherCol = dayRow
    .slice(0, first)
    .findIndex((v) => /^(교사|교사명|성명|선생님)$/.test(text(v)));
  const classCol = dayRow
    .slice(0, first)
    .findIndex((v) =>
      /^(학급|학반|반|학년반)$/.test(text(v).replace(/\s/g, "")),
    );
  const identityCol =
    teacherCol >= 0
      ? teacherCol
      : classCol >= 0
        ? classCol
        : Math.max(0, first - 1);
  const slots: { col: number; day: string; period: number }[] = [];
  let current = "";
  for (let c = first; c < dayRow.length; c++) {
    const d = day(dayRow[c]);
    if (d) current = d;
    else if (text(dayRow[c])) current = ""; // summary columns are never lesson columns
    const p = period(aoa[periods][c]);
    if (current && p) slots.push({ col: c, day: current, period: p });
  }
  const lessons: ParsedLesson[] = [],
    issues: ExcelImportIssue[] = [];
  const sharedTeacherRows: SharedTeacherRow[] = [];
  let paired = false;
  const make = (
    r: number,
    c: number,
    room: string,
    subject: string,
    teacher: string,
    d: string,
    p: number,
    raw: string,
  ) =>
    lessons.push({
      row: r,
      col: c,
      classroom: room,
      subjectName: subject,
      teacherName: teacher,
      day: d,
      period: p,
      raw,
      parts: [subject, teacher],
    });
  for (let r = periods + 1; r < aoa.length; r++) {
    const label = text(aoa[r][identityCol]);
    if (!label) continue;
    const teacher = label.replace(/\s*[（(]\s*\d+\s*[）)]\s*$/, "").trim();
    const room = canonicalClass(label);
    if (teacherCol < 0 && !room) continue;
    const next = aoa[r + 1];
    const pair =
      teacherCol >= 0 &&
      next &&
      !text(next[identityCol]) &&
      slots.some((s) => parseClassList(next[s.col]));
    paired ||= !!pair;
    const rowStart = lessons.length;
    let hasMultipleClasses = false;
    for (const slot of slots) {
      const raw = text(aoa[r][slot.col]);
      const below = pair ? text(next[slot.col]) : "";
      if (!raw && !below) continue;
      if (teacherCol >= 0 && /^[─━―—\-]*[▷▶→]$/.test(raw) && !below) {
        const previous = lessons.filter((lesson) =>
          lesson.row === r && lesson.col === slot.col - 1 &&
          lesson.day === slot.day && lesson.period === slot.period - 1,
        );
        if (previous.length) {
          for (const lesson of previous) make(
            r,
            slot.col,
            lesson.classroom,
            lesson.subjectName,
            teacher,
            slot.day,
            slot.period,
            raw,
          );
          continue;
        }
      }
      let classroom = room,
        subject = "",
        name = teacher;
      if (teacherCol >= 0) {
        const parts = (pair ? [raw, below] : raw.split(/[\r\n]+|\s{2,}/))
          .map(text)
          .filter(Boolean);
        const classParts = parts.filter((v) => parseClassList(v));
        const classrooms = classParts.flatMap((v) => parseClassList(v)!);
        subject = parts.filter((v) => !classParts.includes(v)).join(" ");
        // In paired rows the lower cell must contain only class identifiers.
        // Preserve the existing inline format's multi-part subject names.
        const valid = pair
          ? !!raw && !!parseClassList(below) && classParts.length === 1 && classParts[0] === below
          : true;
        if (!valid || !classrooms.length || !subject || new Set(classrooms).size !== classrooms.length) {
          issues.push({
            code: "UNRECOGNIZED_CELL",
            message: `${excelRef(r, slot.col)}: 학반과 과목을 구분하지 못했습니다 (${raw}${below ? " / " + below : ""}).`,
            hint: "학반(예: 101 또는 1-1)과 과목이 함께 있어야 합니다. 여러 학반은 줄바꿈·쉼표·슬래시로 구분해 주세요.",
            excelRefs: [excelRef(r, slot.col)],
          });
          continue;
        }
        hasMultipleClasses ||= classrooms.length > 1;
        for (const className of classrooms)
          make(r, slot.col, className, subject, name, slot.day, slot.period, raw);
        continue;
      } else {
        const parsed = parseCell(raw);
        subject = parsed.subjectName;
        name = parsed.teacherName;
      }
      make(r, slot.col, classroom!, subject, name, slot.day, slot.period, raw);
    }
    if (hasMultipleClasses) {
      const rowLessons = lessons.slice(rowStart);
      const selected = options.sharedTeacherRows?.includes(r) ?? false;
      sharedTeacherRows.push({
        row: r,
        label: teacher,
        classrooms: [...new Set(rowLessons.map((l) => l.classroom))],
        slotCount: new Set(rowLessons.map((l) => l.col)).size,
        lessonCount: rowLessons.length,
        selected,
      });
      if (selected) {
        for (const lesson of rowLessons) {
          lesson.teacherName = `${teacher} [외부강사·${lesson.classroom}]`;
          lesson.parts = [lesson.subjectName, lesson.teacherName];
          lesson.sharedFixed = true;
        }
      } else {
        issues.push({
          code: "SHARED_LESSON_CONFIRMATION",
          message: `${r + 1}행 ${teacher}: 여러 학급이 함께 적혀 있습니다. 외부강사 공동 수업인지 선택해 주세요.`,
          hint: "공동 수업으로 선택하면 학급별 외부강사 대표 항목과 고정수업으로 등록합니다.",
          excelRefs: [...new Set(rowLessons.map((l) => excelRef(l.row, l.col)))],
        });
      }
    }
    if (pair) r++;
  }
  const result = validateParsedLessons(lessons, {
    classCol: identityCol,
    daysRowIdx: header,
    periodsRowIdx: periods,
    dataStartRow: periods + 1,
  });
  return {
    ...result,
    issues: [...issues, ...result.issues.filter((issue) =>
      issue.code !== "TEACHER_DOUBLE_BOOKED" ||
      !sharedTeacherRows.some((group) => !group.selected &&
        issue.excelRefs?.every((ref) => lessons.some((l) => l.row === group.row && excelRef(l.row, l.col) === ref))),
    )],
    sharedTeacherRows,
    format:
      teacherCol >= 0
        ? paired
          ? "교사별 시간표 · 과목/학반 두 줄"
          : "교사별 시간표"
        : "학급별 시간표",
  };
}
export function inspectTimetableWorkbook(
  workbook: XLSX.WorkBook,
  selectedSheet?: string,
  options: TimetableImportOptions = {},
): InspectTimetableResult {
  const candidates = workbook.SheetNames.map((name) => ({
    name,
    result: inspectSchoolSheet(workbook.Sheets[name], !selectedSheet || selectedSheet === name ? options : {}),
  }));
  const recognized = candidates.filter((c) => c.result.lessons.length > 0);
  const chosen = selectedSheet
    ? candidates.find((c) => c.name === selectedSheet)
    : recognized.length === 1
      ? recognized[0]
      : (recognized[0] ?? candidates[0]);
  if (!chosen) return empty();
  const availableSheets = recognized.map((c) => c.name);
  if (selectedSheet && !candidates.some((c) => c.name === selectedSheet))
    throw new Error("선택한 시트를 찾지 못했습니다.");
  return {
    ...chosen.result,
    sheetName: chosen.name,
    availableSheets,
    issues:
      !selectedSheet && recognized.length > 1
        ? [
            {
              code: "MULTIPLE_SHEETS",
              message:
                "시간표가 여러 시트에 있습니다. 사용할 시트를 선택해 주세요.",
              hint: "다른 학기나 교사별·학급별 표가 중복 등록되지 않도록 하나만 선택합니다.",
            },
          ]
        : chosen.result.issues,
  };
}
